import React, { useRef, useState } from 'react';
import { PDFExport } from '@progress/kendo-react-pdf';
import { format } from 'date-fns';
import { useGetUserQuery } from '../../../../profile/userSlice';
import { supabase } from '../../../../../config/supabaseConfig';

const Certificate = ({ enrollData, setReFetch }) => {
  const completionStatus = 'Completion';
  const { data: userData } = useGetUserQuery();

  const pdfExportComponent = useRef(null);
  const contentArea = useRef(null);

  const [isLoading, setIsLoading] = useState(false);

  const handleExportWithMethod = async () => {
    setIsLoading(true);
    await updateCertificateData();

    // Function to check if images are loaded
    const areImagesLoaded = () => {
      const images = contentArea.current.getElementsByTagName('img');
      return Array.from(images).every((img) => img.complete);
    };

    // Wait for images to load
    const waitForImages = () => {
      return new Promise((resolve) => {
        if (areImagesLoaded()) {
          resolve();
        } else {
          const interval = setInterval(() => {
            if (areImagesLoaded()) {
              clearInterval(interval);
              resolve();
            }
          }, 100);
        }
      });
    };

    try {
      await waitForImages();
      
      // Additional delay to ensure rendering
      await new Promise(resolve => setTimeout(resolve, 500));

      if (pdfExportComponent.current) {
        pdfExportComponent.current.save();
      }
    } catch (error) {
      console.error("Error exporting PDF:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const startDate =
    enrollData?.created_at &&
    format(new Date(enrollData.created_at), 'do MMMM y');

  const endDate = enrollData?.feedback?.sbumitTime
    ? format(new Date(enrollData?.feedback?.sbumitTime), 'do MMMM y')
    : format(Date.now(), 'do MMMM y');

  const updateCertificateData = async () => {
    await supabase
      .from('full_stack_users')
      .update({
        certificate: true,
      })
      .match({ id: enrollData.id });
    setReFetch((pre) => !pre);
  };

  return (
    <>
      <div className='flex flex-col items-center gap-6 lg:gap-10 text-center'>
        <svg
          width='120'
          height='121'
          viewBox='0 0 120 121'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'>
          <path
            d='M116.25 60.5C116.254 56.1935 115.198 51.9521 113.176 48.1498C111.154 44.3476 108.227 41.1013 104.654 38.6971C101.082 36.2929 96.9719 34.8047 92.688 34.3637C88.4041 33.9227 84.0775 34.5425 80.0897 36.1683C76.1019 37.7942 72.5753 40.3763 69.8209 43.6868C67.0666 46.9973 65.169 50.9347 64.2956 55.1517C63.4221 59.3688 63.5996 63.7359 64.8124 67.8682C66.0252 72.0004 68.2361 75.7708 71.25 78.8469V105.5C71.2495 106.14 71.4127 106.769 71.7238 107.327C72.035 107.886 72.4839 108.356 73.0279 108.692C73.5718 109.029 74.1928 109.22 74.8317 109.249C75.4706 109.278 76.1062 109.142 76.6781 108.856L90 102.191L103.322 108.856C103.894 109.142 104.529 109.278 105.168 109.249C105.807 109.22 106.428 109.029 106.972 108.692C107.516 108.356 107.965 107.886 108.276 107.327C108.587 106.769 108.75 106.14 108.75 105.5V78.8469C113.56 73.9517 116.253 67.3626 116.25 60.5ZM90 41.75C93.7084 41.75 97.3335 42.8497 100.417 44.9099C103.5 46.9702 105.904 49.8986 107.323 53.3247C108.742 56.7508 109.113 60.5208 108.39 64.1579C107.666 67.7951 105.88 71.136 103.258 73.7582C100.636 76.3805 97.2951 78.1662 93.6579 78.8897C90.0208 79.6132 86.2508 79.2419 82.8247 77.8227C79.3986 76.4036 76.4702 74.0004 74.4099 70.9169C72.3497 67.8335 71.25 64.2084 71.25 60.5C71.25 55.5272 73.2254 50.7581 76.7417 47.2417C80.2581 43.7254 85.0272 41.75 90 41.75ZM91.6781 94.6437C91.1571 94.383 90.5826 94.2473 90 94.2473C89.4174 94.2473 88.8429 94.383 88.3219 94.6437L78.75 99.4344V84.2141C82.2654 85.8837 86.1083 86.75 90 86.75C93.8917 86.75 97.7346 85.8837 101.25 84.2141V99.4344L91.6781 94.6437ZM63.75 90.5C63.75 91.4946 63.3549 92.4484 62.6517 93.1516C61.9484 93.8549 60.9946 94.25 60 94.25H18.75C16.7609 94.25 14.8532 93.4598 13.4467 92.0533C12.0402 90.6468 11.25 88.7391 11.25 86.75V26.75C11.25 24.7609 12.0402 22.8532 13.4467 21.4467C14.8532 20.0402 16.7609 19.25 18.75 19.25H101.25C103.239 19.25 105.147 20.0402 106.553 21.4467C107.96 22.8532 108.75 24.7609 108.75 26.75C108.75 27.7446 108.355 28.6984 107.652 29.4016C106.948 30.1049 105.995 30.5 105 30.5C104.005 30.5 103.052 30.1049 102.348 29.4016C101.645 28.6984 101.25 27.7446 101.25 26.75H18.75V86.75H60C60.9946 86.75 61.9484 87.1451 62.6517 87.8483C63.3549 88.5516 63.75 89.5054 63.75 90.5ZM56.25 64.25C56.25 65.2446 55.8549 66.1984 55.1516 66.9016C54.4484 67.6049 53.4946 68 52.5 68H33.75C32.7554 68 31.8016 67.6049 31.0984 66.9016C30.3951 66.1984 30 65.2446 30 64.25C30 63.2554 30.3951 62.3016 31.0984 61.5983C31.8016 60.8951 32.7554 60.5 33.75 60.5H52.5C53.4946 60.5 54.4484 60.8951 55.1516 61.5983C55.8549 62.3016 56.25 63.2554 56.25 64.25ZM56.25 49.25C56.25 50.2446 55.8549 51.1984 55.1516 51.9016C54.4484 52.6049 53.4946 53 52.5 53H33.75C32.7554 53 31.8016 52.6049 31.0984 51.9016C30.3951 51.1984 30 50.2446 30 49.25C30 48.2554 30.3951 47.3016 31.0984 46.5983C31.8016 45.8951 32.7554 45.5 33.75 45.5H52.5C53.4946 45.5 54.4484 45.8951 55.1516 46.5983C55.8549 47.3016 56.25 48.2554 56.25 49.25Z'
            fill='#10ABB7'
          />
        </svg>
        <div>
          <p className='text-lg font-semibold'>Congratulations!</p>
          <p>
            You have completed all the requirements & can <br /> proceed to
            download your certificate.
          </p>
        </div>

        <button
          onClick={handleExportWithMethod}
          disabled={isLoading}
          className='bg-primary px-4 py-2 cursor-pointer text-white font-semibold rounded-lg'>
          {isLoading ? 'Preparing...' : 'Download Certificate'}
        </button>
      </div>
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <PDFExport paperSize='auto' landscape={true} ref={pdfExportComponent}>
          <div
            ref={contentArea}
            className='w-[1200px] h-[800px] mx-auto my-8 bg-[url("https://res.cloudinary.com/belong/image/upload/v1690269095/uploaded_resources/WhatsApp_Image_2023-07-25_at_12.36.03_bngscu.jpg")] bg-[length:1180px_650px] bg-center bg-no-repeat'>
            <div className='grid grid-cols-12'>
              <div className='col-span-8 '>
                <div className='px-20 text-left'>
                  <img
                    src='https://res.cloudinary.com/belong/image/upload/v1643351865/uploaded_resources/logo_orzfoc.png'
                    alt='logo'
                    className='h-16 mt-40 object-contain'
                  />
                  <div className='mt-10'>
                    This certificate is proudly presented to
                  </div>

                  <div>
                    <p className='text-4xl font-semibold font-abhaya1 my-6'>
                      {userData?.firstName} {userData?.lastName}
                    </p>

                    <p className='text-neutral-900'>
                      for recognising your dedication and proficiency in
                      <span className='mr-2 font-bold'>
                        {' '}
                        Full Stack Development
                      </span>
                      and the successful completion of the hands-on
                      project, titled
                      <span className='mr-2 font-bold'>
                        {' '}
                        Personal Portfolio Website
                      </span>{' '}
                    </p>
                    <p className='text-neutral-900'>
                      We commend your commitment to upskilling in HTML,
                      CSS, JavaScript, Bootstrap, and Github and
                      demonstrating your skill competency from{' '}
                      <span className='mr-2 font-bold'>{startDate}</span>{' '}
                      to{' '}
                      <span className='mr-2 font-bold'>{endDate} </span>
                    </p>
                    <p className='text-2xl text-primary font-semibold font-abhaya1 mt-6'>
                      Congratulations on this Achievement !
                    </p>
                  </div>
                  {/* signatures */}
                  <div className='space-y-1'>
                    <div className='flex'>
                      <img
                        src='https://res.cloudinary.com/belong/image/upload/v1643353663/uploaded_resources/One_no4yyb.gif'
                        alt='Logo'
                        className='h-16 object-contain'
                      />
                      <img
                        src='https://res.cloudinary.com/belong/image/upload/v1643353575/uploaded_resources/Two_yy5ee6.gif'
                        alt='Logo'
                        className='h-16 object-contain'
                      />
                    </div>
                    <hr />
                    <p className='text-[#767676] '>Belong Co - Founders</p>
                  </div>
                </div>
              </div>
              {/* right side */}
              <div className='col-span-4'>
                <div className='w-[60%]  ml-20 mb-6 mt-24'>
                  {completionStatus === 'Completion' ? (
                    <img
                      src='https://res.cloudinary.com/belong/image/upload/v1690269860/uploaded_resources/Frame_904_lmc8ws.png'
                      alt='banner'
                    />
                  ) : (
                    <img
                      src='https://res.cloudinary.com/belong/image/upload/v1690269731/uploaded_resources/Frame_710_l9bbsx.png'
                      alt='banner'
                    />
                  )}
                </div>
                <div className='space-y-2 text-right text-sm mr-20'>
                  <p className='text-primary font-semibold'>
                    Future Skills Bootcamp in Full Stack
                  </p>
                  <p className='text-[#767676] font-medium'>
                    Certificate associated with Belong.education
                  </p>
                </div>
              </div>
            </div>
          </div>
        </PDFExport>
      </div>
    </>
  );
};

export default Certificate;
